/**
 * MHA collection / Readiness Resources catalog — filters, search, product grid.
 * Expects window.MhaCollectionPageData[sectionId] set by Liquid.
 */
(function () {
  const PAGE_SIZE = 18;

  const SUBJECT_ALIASES = {
    "Words & Stories": ["Words & Stories", "Language & Literacy", "Literacy"],
    "Numbers & Patterns": ["Numbers & Patterns", "Math"],
    "Science & Curiosity": ["Science & Curiosity", "Science", "STEAM"],
    "Create & Move": ["Create & Move", "Art", "Creativity", "Art/Music/Movement"],
    "Feelings, Focus & Routines": ["Feelings, Focus & Routines", "SEL", "Feelings"],
  };

  const SUBJECT_SKILL_DEFAULTS = {
    "Words & Stories": ["Letter sounds", "Rhyming", "Vocabulary"],
    "Numbers & Patterns": ["Counting", "Patterns", "Shapes"],
    "Science & Curiosity": ["Observe", "Ask why", "Discover"],
    "Create & Move": ["Art", "Music", "Imagination"],
    "Feelings, Focus & Routines": ["Feelings", "Friendship", "Self-care"],
  };

  const HOW_TO_USE_BY_TYPE = {
    printable:
      "Print the pages and use during circle time, centers, or at-home practice—no prep beyond scissors or crayons.",
    ebook:
      "Open on a tablet or print selected pages. Read aloud together, then revisit key words and pictures.",
    music:
      "Play during transitions, movement breaks, or circle time. Sing along and connect lyrics to the day’s skill.",
    curriculum:
      "Follow the weekly path in the curriculum. Pair each lesson with a printable or song for extra practice.",
    "lesson-builder":
      "Open Lesson Builder, pick a skill and age band, then assign or print the finished lesson.",
    ai: "Generate a lesson for your skill and age band, review it, then use it in class or at home.",
    observational:
      "Use the notes pack while children play or practice. Capture readiness observations for families or teams.",
    other: "Open the resource, match it to your age band and skill, then use it in a short learning moment.",
  };

  function formatMoney(cents) {
    if (cents == null || Number.isNaN(cents)) return "";
    return "$" + (Number(cents) / 100).toFixed(2).replace(/\.00$/, "");
  }

  function parseTags(tags) {
    let mhaType = null;
    const ages = [];
    const subjects = [];
    const skills = [];
    let howToUseTag = null;
    for (const tag of tags || []) {
      const s = String(tag);
      const low = s.toLowerCase();
      if (low.startsWith("mha-type:")) {
        mhaType = s.split(":").slice(1).join(":").trim().toLowerCase();
      } else if (low.startsWith("mha-age:")) {
        const n = parseInt(s.split(":")[1], 10);
        if (!Number.isNaN(n)) ages.push(n);
      } else if (low.startsWith("mha-subject:")) {
        subjects.push(s.split(":").slice(1).join(":").trim());
      } else if (low.startsWith("mha-skill:")) {
        skills.push(s.split(":").slice(1).join(":").trim());
      } else if (low.startsWith("mha-howto:") || low.startsWith("mha-how-to-use:")) {
        howToUseTag = s.split(":").slice(1).join(":").trim();
      }
    }
    return { mhaType, ages, subjects, skills, howToUseTag };
  }

  function deriveType(product) {
    const { mhaType } = parseTags(product.tags);
    if (mhaType && ["printable", "ebook", "music", "other"].includes(mhaType)) {
      return mhaType === "other" ? inferFromProductType(product) : mhaType;
    }
    return inferFromProductType(product);
  }

  function inferFromProductType(product) {
    const pt = (product.product_type || "").toLowerCase();
    if (pt.includes("ebook") || pt.includes("e-book") || pt === "book" || pt.includes("book")) return "ebook";
    if (pt.includes("music") || pt.includes("song") || pt.includes("video")) return "music";
    if (pt.includes("print")) return "printable";
    return "other";
  }

  function shouldShowInCatalog(product) {
    const tags = (product.tags || []).map((x) => String(x).toLowerCase());
    return !tags.includes("mha-hide-from-catalog") && !tags.includes("mha-hide-from-grid");
  }

  function isAiLessonBuilder(product) {
    const title = (product.title || "").toLowerCase();
    const handle = (product.handle || "").toLowerCase();
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    return (
      title.includes("ai-assisted") ||
      title.includes("ai lesson") ||
      handle.includes("ai-assisted") ||
      handle.includes("ai-lesson") ||
      tags.some((t) => t.includes("ai-lesson") || t.includes("ai lesson"))
    );
  }

  function isManualLessonBuilder(product) {
    if (isAiLessonBuilder(product)) return false;
    const title = (product.title || "").toLowerCase();
    const handle = (product.handle || "").toLowerCase();
    return (
      title.includes("lesson builder") ||
      title.includes("manual build") ||
      handle.includes("lesson-builder-manual") ||
      handle.includes("manual-build")
    );
  }

  function isCurriculumSubscription(product) {
    if (isAiLessonBuilder(product) || isManualLessonBuilder(product)) return false;
    const title = (product.title || "").toLowerCase();
    const handle = (product.handle || "").toLowerCase();
    return (
      title.includes("curriculum") ||
      title.includes("humblebee") ||
      title.includes("academy") ||
      handle.includes("academy") ||
      handle.includes("curriculum")
    );
  }

  function isSubscriptionProduct(product) {
    if (!product) return false;
    const tags = (product.tags || []).map((x) => String(x).toLowerCase());
    const { mhaType } = parseTags(product.tags);
    if (mhaType === "subscription") return true;
    const pt = (product.product_type || "").toLowerCase();
    if (pt.includes("subscription")) return true;
    if (isAiLessonBuilder(product) || isManualLessonBuilder(product) || isCurriculumSubscription(product)) {
      return true;
    }
    const title = (product.title || "").toLowerCase();
    if (
      title.includes("curriculum") ||
      title.includes("lesson builder") ||
      title.includes("ai lesson")
    ) {
      return true;
    }
    return false;
  }

  function productCategory(product) {
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    if (tags.some((t) => t.includes("observational") || t.includes("reporting"))) {
      return "observational";
    }
    if (isSubscriptionProduct(product)) {
      if (isAiLessonBuilder(product)) return "ai";
      if (isManualLessonBuilder(product)) return "lesson-builder";
      return "curriculum";
    }
    const type = deriveType(product);
    if (type === "printable") return "printables";
    if (type === "ebook") return "ebooks";
    if (type === "music") return "songs";
    return "other";
  }

  function displayTag(product) {
    const title = (product.title || "").toLowerCase();
    const pt = (product.product_type || "").toLowerCase();
    if (title.includes("gift card") || pt.includes("gift")) return "Gift Card";

    const cat = productCategory(product);
    const map = {
      curriculum: "Curriculum",
      ai: "AI Builder",
      "lesson-builder": "Lesson Builder",
      printables: "Printable",
      ebooks: "eBook",
      songs: "Song / Video",
      observational: "Reporting",
      free: "Free",
      other: "Resource",
    };
    return map[cat] || "Resource";
  }

  function normalizeSubjectLabel(name) {
    const n = String(name || "").trim().toLowerCase();
    const map = {
      "language & literacy": "Words & Stories",
      literacy: "Words & Stories",
      math: "Numbers & Patterns",
      science: "Science & Curiosity",
      steam: "Science & Curiosity",
      art: "Create & Move",
      creativity: "Create & Move",
      "art/music/movement": "Create & Move",
      sel: "Feelings, Focus & Routines",
      feelings: "Feelings, Focus & Routines",
    };
    return map[n] || name;
  }

  function subjectLabels(product) {
    const { subjects } = parseTags(product.tags);
    if (subjects.length) {
      return [...new Set(subjects.map(normalizeSubjectLabel).filter(Boolean))];
    }
    return [];
  }

  function subjectLine(product) {
    const labels = subjectLabels(product);
    if (labels.length) return labels.join(", ");
    const cat = productCategory(product);
    if (cat === "curriculum") return "School Readiness";
    if (cat === "observational") return "School Readiness";
    if (deriveType(product) === "ebook") return "Words & Stories";
    return "Learning";
  }

  function ageLabel(product) {
    if (product.age_range) {
      const raw = String(product.age_range).trim();
      if (!raw) return "Ages 3–6";
      return /^ages?\b/i.test(raw) ? raw : "Ages " + raw;
    }
    const { ages } = parseTags(product.tags);
    if (ages.length) {
      const sorted = [...new Set(ages)].sort((a, b) => a - b);
      if (sorted.length === 1) return "Ages " + sorted[0];
      return "Ages " + sorted[0] + "–" + sorted[sorted.length - 1];
    }
    return "Ages 3–6";
  }

  function ageMatchesFilter(product, ageFilter) {
    if (!ageFilter) return true;
    const target = parseInt(ageFilter, 10);
    if (Number.isNaN(target)) return true;
    const { ages } = parseTags(product.tags);
    if (ages.length) return ages.includes(target);
    if (product.age_range) {
      const nums = String(product.age_range).match(/\d+/g);
      if (!nums || !nums.length) return true;
      const values = nums.map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n));
      if (values.length === 1) return values[0] === target;
      if (values.length >= 2) {
        const mn = Math.min(...values);
        const mx = Math.max(...values);
        return target >= mn && target <= mx;
      }
    }
    return target >= 3 && target <= 6;
  }

  function formatLabel(product) {
    if (product.format) return String(product.format).trim();
    const cat = productCategory(product);
    const type = deriveType(product);
    if (cat === "curriculum") return "Subscription curriculum";
    if (cat === "lesson-builder" || cat === "ai") return "Lesson Builder plan";
    if (cat === "observational") return "Observation notes";
    if (type === "ebook") return "Digital book (PDF)";
    if (type === "music") return "Digital audio / video";
    if (type === "printable") return "Printable PDF";
    return "Digital download";
  }

  function formatFilterValue(product) {
    const cat = productCategory(product);
    if (cat === "printables") return "printable";
    if (cat === "ebooks") return "ebook";
    if (cat === "songs") return "music";
    if (cat === "curriculum") return "curriculum";
    if (cat === "lesson-builder" || cat === "ai") return "lesson-builder";
    if (cat === "observational") return "observational";
    return deriveType(product);
  }

  function skillTags(product) {
    const { skills, subjects } = parseTags(product.tags);
    if (skills.length) return skills.slice(0, 4);
    const primary = subjects.map(normalizeSubjectLabel)[0];
    if (primary && SUBJECT_SKILL_DEFAULTS[primary]) {
      return SUBJECT_SKILL_DEFAULTS[primary].slice(0, 3);
    }
    const line = subjectLine(product);
    if (SUBJECT_SKILL_DEFAULTS[line]) return SUBJECT_SKILL_DEFAULTS[line].slice(0, 3);
    const cat = productCategory(product);
    if (cat === "curriculum" || cat === "observational") {
      return ["Independence", "Classroom routines", "Next steps"];
    }
    return [];
  }

  function howToUseCopy(product) {
    if (product.how_to_use) return String(product.how_to_use).trim();
    const { howToUseTag } = parseTags(product.tags);
    if (howToUseTag) return howToUseTag;
    const cat = productCategory(product);
    if (HOW_TO_USE_BY_TYPE[cat]) return HOW_TO_USE_BY_TYPE[cat];
    return HOW_TO_USE_BY_TYPE[deriveType(product)] || HOW_TO_USE_BY_TYPE.other;
  }

  function imageOrientationClass(product) {
    const w = Number(product.featured_image_width);
    const h = Number(product.featured_image_height);
    if (!w || !h) return "product-art-img--pending";
    if (w > h * 1.05) return "product-art-img--landscape";
    if (h > w * 1.05) return "product-art-img--portrait";
    return "product-art-img--square";
  }

  function applyImageOrientation(img) {
    img.classList.remove(
      "product-art-img--pending",
      "product-art-img--landscape",
      "product-art-img--portrait",
      "product-art-img--square"
    );
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return;
    if (w > h * 1.05) img.classList.add("product-art-img--landscape");
    else if (h > w * 1.05) img.classList.add("product-art-img--portrait");
    else img.classList.add("product-art-img--square");
  }

  function bindImageOrientations(root) {
    if (!root) return;
    root.querySelectorAll(".product-art-img--pending").forEach((img) => {
      if (img.complete) applyImageOrientation(img);
      else img.addEventListener("load", () => applyImageOrientation(img), { once: true });
    });
  }

  function cardArt(product) {
    if (product.featured_image) {
      const orient = imageOrientationClass(product);
      return `<img class="product-art-img ${orient}" src="${escapeAttr(product.featured_image)}" alt="" loading="lazy" width="360" height="180">`;
    }
    const cat = productCategory(product);
    const emoji = {
      curriculum: "📚",
      ai: "AI",
      "lesson-builder": "🧩",
      printables: "AZ",
      ebooks: "☀",
      songs: "♫",
      observational: "📝",
      free: "★",
      other: "🌐",
    };
    const label = emoji[cat] || "📄";
    if (label.length <= 3) return label;
    return `<span aria-hidden="true">${label}</span>`;
  }

  function displayPrice(product) {
    if (Number(product.price) === 0) return "Free";
    const title = (product.title || "").toLowerCase();
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    if (
      isSubscriptionProduct(product) ||
      title.includes("/mo") ||
      tags.some((t) => t.includes("subscription"))
    ) {
      const monthly = formatMoney(product.price);
      return monthly ? "From " + monthly + "/mo" : "Subscription";
    }
    return formatMoney(product.price);
  }

  function escapeHtml(s) {
    if (!s) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, "&#39;");
  }

  function renderSkillTags(product) {
    const skills = skillTags(product);
    if (!skills.length) return "";
    return `<div class="skill-tags">${skills
      .map((skill) => `<span>${escapeHtml(skill)}</span>`)
      .join("")}</div>`;
  }

  function renderCard(product) {
    const artClass = "product-art" + (product.featured_image ? " product-art--image" : "");
    const url = escapeAttr(product.url);
    const facts = [ageLabel(product), subjectLine(product), formatLabel(product)].join(" · ");
    const howto = howToUseCopy(product);
    return `<a class="product-card product-card--link" href="${url}">
      <div class="${artClass}">
        <span class="tag tag--overlay">${escapeHtml(displayTag(product))}</span>
        ${cardArt(product)}
      </div>
      <div class="product-body">
        <h3 class="serif">${escapeHtml(product.title)}</h3>
        <p class="product-card-facts">${escapeHtml(facts)}</p>
        ${renderSkillTags(product)}
        ${howto ? `<p class="product-howto"><span class="product-howto__label">How to use</span> ${escapeHtml(howto)}</p>` : ""}
        <div class="product-meta">
          <strong>${escapeHtml(displayPrice(product))}</strong>
          <span class="product-card-cta">View →</span>
        </div>
      </div>
    </a>`;
  }

  function featuredIdSet(data) {
    return new Set((data.featured || []).map((p) => String(p.id)));
  }

  function mergeCatalog(data) {
    const extra = data.extra || {};
    const seen = new Set();
    const out = [];
    const exclude = featuredIdSet(data);
    function pushList(list) {
      if (!Array.isArray(list)) return;
      for (const p of list) {
        if (!p || p.id == null) continue;
        const id = String(p.id);
        if (seen.has(id) || exclude.has(id)) continue;
        seen.add(id);
        out.push(p);
      }
    }
    pushList(data.primary);
    for (const key of ["subscriptions", "printables", "ebooks", "music", "free"]) {
      pushList(extra[key]);
    }
    return out.filter(shouldShowInCatalog);
  }

  function matchesCategory(product, category) {
    if (category === "free") return Number(product.price) === 0;
    const cat = productCategory(product);
    if (category === "lesson-builder") return cat === "lesson-builder" || cat === "ai";
    return cat === category;
  }

  function matchesSubject(product, subject) {
    if (!subject) return true;
    const aliases = SUBJECT_ALIASES[subject] || [subject];
    const aliasSet = new Set(aliases.map((a) => String(a).toLowerCase()));
    const labels = subjectLabels(product).map((s) => String(s).toLowerCase());
    if (labels.some((s) => aliasSet.has(s) || s === subject.toLowerCase())) return true;
    return (product.tags || []).some((t) => aliasSet.has(String(t).trim().toLowerCase()));
  }

  function matchesFormat(product, format) {
    if (!format) return true;
    return formatFilterValue(product) === format;
  }

  function filterByCategory(list, category) {
    if (!category || category === "all") return list;
    return list.filter((p) => matchesCategory(p, category));
  }

  function applyResourceFilters(list, filters) {
    let out = list;
    if (filters.subject) out = out.filter((p) => matchesSubject(p, filters.subject));
    if (filters.format) out = out.filter((p) => matchesFormat(p, filters.format));
    if (filters.age) out = out.filter((p) => ageMatchesFilter(p, filters.age));
    return out;
  }

  function productSearchText(product) {
    const tags = (product.tags || []).join(" ");
    const skills = skillTags(product).join(" ");
    return [
      product.title,
      product.description,
      product.product_type,
      product.handle,
      tags,
      displayTag(product),
      ageLabel(product),
      subjectLine(product),
      formatLabel(product),
      howToUseCopy(product),
      skills,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  }

  function productsForCategory(catalog, category, data) {
    const filtered = filterByCategory(catalog, category);
    if (!category || category === "all" || !data || !Array.isArray(data.featured)) {
      return filtered;
    }
    const seen = new Set(filtered.map((p) => String(p.id)));
    const merged = [...filtered];
    for (const product of data.featured) {
      if (!product || product.id == null) continue;
      if (!shouldShowInCatalog(product) || !matchesCategory(product, category)) continue;
      const id = String(product.id);
      if (seen.has(id)) continue;
      merged.unshift(product);
      seen.add(id);
    }
    return merged;
  }

  function applySearch(list, search, data, category) {
    const q = (search || "").trim().toLowerCase();
    if (!q) return list;

    const seen = new Set(list.map((p) => String(p.id)));
    const extras = [];
    if (data && Array.isArray(data.featured)) {
      for (const product of data.featured) {
        if (!product || product.id == null) continue;
        if (!shouldShowInCatalog(product) || !matchesCategory(product, category)) continue;
        if (!productSearchText(product).includes(q)) continue;
        const id = String(product.id);
        if (seen.has(id)) continue;
        seen.add(id);
        extras.push(product);
      }
    }

    const matched = list.filter((p) => productSearchText(p).includes(q));
    return extras.length ? [...extras, ...matched] : matched;
  }

  function sortProducts(list, sort) {
    if (!sort || sort === "featured") return list;
    const arr = list.slice();
    if (sort === "az") {
      arr.sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
      );
    } else if (sort === "za") {
      arr.sort((a, b) =>
        (b.title || "").localeCompare(a.title || "", undefined, { sensitivity: "base" })
      );
    } else if (sort === "price-asc") {
      arr.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sort === "price-desc") {
      arr.sort((a, b) => Number(b.price) - Number(a.price));
    }
    return arr;
  }

  function renderProductGrid(grid, products, options) {
    if (!grid) return;
    const hasSearch = options && options.search;
    grid.innerHTML =
      products.length === 0
        ? `<div class="mha-collection-empty" role="status">
            <h3 class="serif">No resources found</h3>
            <p>${
              hasSearch
                ? "Try a different search term, subject, age, or format."
                : "Try a different subject, age, or format filter."
            }</p>
          </div>`
        : products.map(renderCard).join("");
    bindImageOrientations(grid);
  }

  function initSection(root) {
    const sectionId = root.dataset.sectionId;
    const data = window.MhaCollectionPageData && window.MhaCollectionPageData[sectionId];
    if (!data) return;

    const state = {
      category: "all",
      subject: "",
      format: "",
      age: "",
      sort: "featured",
      search: "",
      page: 1,
      allProducts: data.mergeCatalog ? mergeCatalog(data) : data.primary || [],
    };

    const els = {
      featuredGrid: root.querySelector("[data-mha-featured-grid]"),
      grid: root.querySelector("[data-mha-grid]"),
      count: root.querySelector("[data-mha-count]"),
      loadMore: root.querySelector("[data-mha-load-more]"),
      sortSelect: root.querySelector("[data-mha-sort]"),
      searchInput: root.querySelector("[data-mha-search]"),
      subjectSelect: root.querySelector("[data-mha-subject]"),
      formatSelect: root.querySelector("[data-mha-format]"),
      ageSelect: root.querySelector("[data-mha-age]"),
      filters: root.querySelectorAll("[data-mha-filter]"),
      pills: root.querySelectorAll("[data-mha-pill]"),
    };

    if (els.featuredGrid && Array.isArray(data.featured) && data.featured.length) {
      renderProductGrid(els.featuredGrid, data.featured);
    }

    function setCategory(category) {
      state.category = category;
      state.page = 1;
      els.filters.forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.mhaFilter === category);
      });
      els.pills.forEach((btn) => {
        btn.classList.toggle("is-active", btn.dataset.mhaPill === category);
      });
      render();
      if (category !== "all") {
        const products = root.querySelector("#products");
        if (products) products.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    function setSubject(subject) {
      state.subject = subject || "";
      state.page = 1;
      root.querySelectorAll("[data-mha-subject-card]").forEach((el) => {
        el.classList.toggle("is-active", el.dataset.mhaSubjectCard === state.subject);
      });
      if (els.subjectSelect && els.subjectSelect.value !== state.subject) {
        els.subjectSelect.value = state.subject;
      }
      render();
      if (state.subject) {
        const products = root.querySelector("#products");
        if (products) products.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    function render() {
      const filtered = sortProducts(
        applyResourceFilters(
          applySearch(
            productsForCategory(state.allProducts, state.category, data),
            state.search,
            data,
            state.category
          ),
          { subject: state.subject, format: state.format, age: state.age }
        ),
        state.sort
      );
      const visible = filtered.slice(0, state.page * PAGE_SIZE);
      const hasSearch = !!(state.search && state.search.trim());

      if (els.count) {
        els.count.textContent =
          filtered.length + (filtered.length === 1 ? " resource" : " resources");
      }

      if (!els.grid) return;

      renderProductGrid(els.grid, visible, { search: hasSearch });

      if (els.loadMore) {
        if (visible.length < filtered.length) {
          els.loadMore.hidden = false;
          els.loadMore.textContent =
            "Load more · " + (filtered.length - visible.length) + " remaining";
        } else {
          els.loadMore.hidden = true;
        }
      }
    }

    els.filters.forEach((btn) => {
      btn.addEventListener("click", () => setCategory(btn.dataset.mhaFilter || "all"));
    });

    root.querySelectorAll("[data-mha-category]").forEach((el) => {
      el.addEventListener("click", (e) => {
        const cat = el.dataset.mhaCategory;
        if (!cat) return;
        e.preventDefault();
        setCategory(cat);
      });
    });

    root.querySelectorAll("[data-mha-subject-card]").forEach((el) => {
      el.addEventListener("click", (e) => {
        const subject = el.dataset.mhaSubjectCard;
        if (!subject) return;
        e.preventDefault();
        setSubject(state.subject === subject ? "" : subject);
      });
    });

    els.pills.forEach((btn) => {
      btn.addEventListener("click", () => setCategory(btn.dataset.mhaPill || "all"));
    });

    if (els.loadMore) {
      els.loadMore.addEventListener("click", () => {
        state.page += 1;
        render();
      });
    }

    if (els.sortSelect) {
      els.sortSelect.addEventListener("change", () => {
        state.sort = els.sortSelect.value || "featured";
        state.page = 1;
        render();
      });
    }

    if (els.searchInput) {
      els.searchInput.addEventListener("input", () => {
        state.search = els.searchInput.value;
        state.page = 1;
        render();
      });
    }

    if (els.subjectSelect) {
      els.subjectSelect.addEventListener("change", () => {
        setSubject(els.subjectSelect.value || "");
      });
    }

    if (els.formatSelect) {
      els.formatSelect.addEventListener("change", () => {
        state.format = els.formatSelect.value || "";
        state.page = 1;
        render();
      });
    }

    if (els.ageSelect) {
      els.ageSelect.addEventListener("change", () => {
        state.age = els.ageSelect.value || "";
        state.page = 1;
        render();
      });
    }

    setCategory("all");
  }

  function boot() {
    document.querySelectorAll(".mha-collection-page[data-section-id]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
